import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteLocationNormalizedGeneric,
} from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { Eatfresh_Basic_User_Type_Enum } from '@/gql/graphql';
import { useSharedStore } from '@/stores/shared_store';
import { useWarehouseAssignmentStore } from '@/stores/warehouse_assignment';
import { fireEvent } from '@/firebase';
import { installHistoryTraversalTracking } from '@/utils/historyTraversal';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // General
    {
      path: '/',
      redirect: '/customer/map',
    },
    {
      path: '/citypopffm',
      name: 'citypopffm',
      component: () => import('../views/general/CityPopLanding.vue'),
    },
    {
      path: '/flyers',
      redirect: '/customer/register',
    },
    {
      path: '/password-reset',
      name: 'password-reset',
      component: () =>
        import(
          '@/components/forms/password_reset/CredentialResetConfirmationForm.vue'
        ),
    },
    {
      path: '/activate-account',
      name: 'activate-account',
      component: () => import('../views/general/ActivateAccount.vue'),
    },
    // Admin
    {
      path: '/admin',
      meta: {
        roles: [Eatfresh_Basic_User_Type_Enum.Admin],
      },
      component: () => import('../views/admin/AdminLayout.vue'),
      children: [
        {
          path: '',
          name: 'admin-home',
          component: () => import('../views/admin/AdminHomepage.vue'),
        },
        {
          meta: {
            // only allow unauthenticated users
            roles: [null],
          },
          path: 'login',
          name: 'admin-login',
          component: () => import('@/components/LoginModal.vue'),
        },
        {
          path: 'profile',
          name: 'admin-profile',
          component: () => import('../views/admin/AdminUserProfile.vue'),
        },
        {
          // Old route name kept as a redirect alias so existing links/tests
          // still resolve to the reworked Overview sub-page.
          path: 'kpi-dashboard',
          name: 'admin-kpi-dashboard',
          redirect: { name: 'admin-kpi-overview' },
        },
        {
          path: 'kpi',
          component: () => import('../views/admin/AdminKpiLayout.vue'),
          redirect: { name: 'admin-kpi-overview' },
          children: [
            {
              path: 'overview',
              name: 'admin-kpi-overview',
              component: () => import('../views/admin/AdminKpiOverview.vue'),
            },
            {
              path: 'locations',
              name: 'admin-kpi-locations',
              component: () => import('../views/admin/AdminKpiLocations.vue'),
            },
            {
              path: 'products',
              name: 'admin-kpi-products',
              component: () => import('../views/admin/AdminKpiProducts.vue'),
            },
            {
              path: 'customers',
              name: 'admin-kpi-customers',
              component: () => import('../views/admin/AdminKpiCustomers.vue'),
            },
            {
              path: 'costs',
              name: 'admin-kpi-costs',
              component: () => import('../views/admin/AdminKpiCosts.vue'),
            },
          ],
        },
        {
          // Old route name kept as a redirect alias so existing links/tests
          // still resolve to the reworked Overview sub-page.
          path: 'operations-health',
          name: 'admin-operations-health',
          redirect: { name: 'admin-operations-overview' },
        },
        {
          path: 'operations',
          component: () => import('../views/admin/AdminOperationsLayout.vue'),
          redirect: { name: 'admin-operations-overview' },
          children: [
            {
              path: 'overview',
              name: 'admin-operations-overview',
              component: () => import('../views/admin/AdminOperationsHealth.vue'),
            },
            {
              path: 'monitoring',
              name: 'admin-operations-monitoring',
              component: () => import('../views/admin/AdminOperationsMonitoring.vue'),
            },
            {
              path: 'alert-mutes',
              name: 'admin-operations-alert-mutes',
              component: () => import('../views/admin/AdminOperationsAlertMutes.vue'),
            },
          ],
        },
        {
          path: 'article-management',
          name: 'admin-article-management',
          component: () => import('../views/admin/AdminArticleTable.vue'),
        },
        {
          path: 'article-tasks',
          name: 'admin-article-tasks',
          component: () => import('../views/admin/AdminArticleTasks.vue'),
        },
        {
          path: 'rfid-tag-management',
          name: 'admin-rfid-tag-management',
          component: () => import('../views/admin/AdminRfidTable.vue'),
        },
        {
          path: 'fridge-management',
          component: () => import('../views/admin/AdminFridgesLayout.vue'),
          redirect: { name: 'admin-fridges-table' },
          children: [
            {
              path: 'table',
              name: 'admin-fridges-table',
              component: () => import('../views/admin/AdminFridgeTable.vue'),
            },
            {
              path: 'layouts',
              name: 'admin-fridges-layouts',
              component: () =>
                import('../views/admin/AdminFridgeLayoutsTable.vue'),
            },
            {
              path: 'layouts/:id',
              name: 'admin-fridge-layout',
              component: () => import('../views/admin/AdminFridgeLayout.vue'),
            },
            {
              path: 'stock/:id',
              name: 'admin-fridge-stock',
              component: () =>
                import('../views/admin/AdminFridgeStockTable.vue'),
            },
          ],
        },
        {
          path: 'order/:id',
          name: 'admin-order-positions',
          component: () =>
            import('../views/admin/AdminOrderPositionsTable.vue'),
        },
        {
          path: 'user-management',
          name: 'admin-user-management',
          component: () => import('../views/admin/AdminUserTable.vue'),
        },
        {
          path: 'pos-transaction-management',
          name: 'admin-pos-transaction-management',
          component: () => import('../views/admin/AdminPosTransactionTable.vue'),
        },
        {
          path: 'b2b-customer-management',
          name: 'admin-b2b-customer-management',
          component: () => import('../views/admin/AdminB2bCustomerTable.vue'),
        },
        {
          path: 'warehouse-management',
          name: 'admin-warehouse-management',
          component: () => import('../views/admin/AdminWarehouseTable.vue'),
        },
        {
          path: 'places-management',
          name: 'admin-places-management',
          component: () => import('../views/admin/AdminPlacesTable.vue'),
        },
        {
          path: 'order-management',
          name: 'admin-order-management',
          component: () =>
            import('../views/admin/AdminOrderTable.vue'),
        },
        {
          path: 'new-warehouse-order',
          name: 'admin-new-warehouse-order',
          component: () => import('../views/admin/AdminNewWarehouseOrder.vue'),
        },
        {
          path: 'edit-warehouse-order/:id',
          name: 'edit-warehouse-order',
          component: () => import('../views/admin/AdminEditWarehouseOrder.vue'),
        },
        {
          path: 'warehouse-order/:id',
          name: 'admin-warehouse-order-positions',
          component: () =>
            import('../views/admin/AdminWarehouseOrderPositionsTable.vue'),
        },
        {
          path: 'vat-categories',
          name: 'admin-vat-categories',
          component: () => import('../views/admin/AdminVatTable.vue'),
        },

        {
          path: 'rfid-scanners',
          name: 'admin-rfid-scanners',
          component: () => import('../views/admin/AdminRfidScannersTable.vue'),
        },
        {
          path: 'cost-configuration',
          name: 'admin-cost-configuration',
          component: () => import('../views/admin/AdminCostConfiguration.vue'),
        },
        {
          path: 'price-locks',
          name: 'admin-price-locks',
          component: () =>
            import('../views/admin/AdminPriceLockManagement.vue'),
        },
      ],
    },
    {
      path: '/mock',
      meta: { roles: [Eatfresh_Basic_User_Type_Enum.Admin] },
      children: [
        {
          path: 'login',
          name: 'mock-login',
          component: () => import('../components/LoginModal.vue'),
        },
        {
          path: 'fridge-select',
          name: 'mock-fridge-select',
          component: () => import('../views/general/FridgeMockSelection.vue'),
        },
        {
          path: 'fridge/:given_id',
          name: 'mock-fridge',
          component: () => import('../views/mock/MockFridge.vue'),
        },
        {
          path: 'scanner',
          name: 'mock-scanner',
          component: () => import('../views/mock/MockScanner.vue'),
        },
        {
          path: 'photo-camera',
          name: 'mock-photo-camera',
          component: () => import('@/views/mock/PhotoCamera.vue'),
        },
        {
          path: 'u-scanner',
          name: 'mock-u-scanner',
          component: () => import('@/views/mock/UScanner.vue'),
        },
      ],
    },
    // Customer
    {
      path: '/customer',
      meta: {
        roles: [
          Eatfresh_Basic_User_Type_Enum.Customer,
          Eatfresh_Basic_User_Type_Enum.Anonymous,
          null, // Allow unauthenticated users
        ],
      },
      component: () => import('../views/customer/CustomerLayout.vue'),
      children: [
        {
          path: '',
          redirect: () => {
            return { name: 'customer-map' };
          },
        },
        {
          path: 'fridge-stock/:given_id_or_selfly_device_code',
          name: 'customer-fridge-stock',
          component: () => import('../views/customer/CustomerFridgeStock.vue'),
        },
        {
          path: 'fridge-stock/payment/:given_id_or_selfly_device_code',
          name: 'customer-payment',
          component: () => import('../views/customer/CustomerPaymentCta.vue'),
        },
        {
          meta: {
            // only allow unauthenticated users
            roles: [null],
          },
          path: 'login',
          name: 'customer-login',
          component: () => import('../views/customer/CustomerLoginScreen.vue'),
        },
        {
          path: 'register',
          name: 'customer-register',
          component: () =>
            import('../views/customer/CustomerRegisterScreen.vue'),
        },
        {
          path: 'purchase-confirmation',
          name: 'customer-purchase-confirmation',
          component: () =>
            import('../views/customer/CustomerPurchaseComplete.vue'),
        },
        {
          path: 'order-invoice/:orderId',
          name: 'customer-order-invoice',
          component: () => import('../views/customer/CustomerInvoice.vue'),
        },
        {
          path: 'order-canceled',
          name: 'customer-order-canceled',
          component: () =>
            import('../views/customer/CustomerOrderCanceled.vue'),
        },
        {
          path: 'order-disturbance',
          name: 'customer-order-disturbance',
          component: () =>
            import('../views/customer/CustomerOrderDisturbance.vue'),
        },
        {
          path: 'order-timeout',
          name: 'customer-order-timeout',
          component: () => import('../views/customer/CustomerOrderTimeout.vue'),
        },
        {
          path: 'checkin-failed',
          name: 'customer-checkin-failed',
          component: () =>
            import('../views/customer/CustomerCheckinFailed.vue'),
        },
        {
          path: 'fridge-selection',
          name: 'customer-fridge-selection',
          component: () =>
            import('../views/customer/Navbar/CustomerFridgeScanner.vue'),
        },
        {
          path: 'profile',
          name: 'customer-profile',
          redirect: () => {
            return { name: 'customer-map' };
          },
        },
        {
          path: 'settings',
          children: [
            {
              path: '',
              name: 'customer-settings',
              component: () =>
                import('../views/customer/Navbar/CustomerSettings.vue'),
            },
            {
              meta: {
                roles: [Eatfresh_Basic_User_Type_Enum.Customer],
              },
              path: 'payment',
              name: 'customer-payment-settings',
              component: () =>
                import(
                  '../views/customer/settings/CustomerPaymentSettings.vue'
                ),
            },
            {
              meta: {
                roles: [Eatfresh_Basic_User_Type_Enum.Customer],
              },
              path: 'account',
              name: 'customer-account-settings',
              component: () =>
                import(
                  '../views/customer/settings/CustomerAccountSettings.vue'
                ),
            },
            {
              meta: {
                roles: [Eatfresh_Basic_User_Type_Enum.Customer],
              },
              path: 'security',
              name: 'customer-security-settings',
              component: () =>
                import(
                  '../views/customer/settings/CustomerSecuritySettings.vue'
                ),
            },
          ],
        },
        {
          path: 'orders',
          name: 'customer-orders',
          component: () =>
            import('../views/customer/Navbar/CustomerOrders.vue'),
        },
        {
          path: 'map',
          name: 'customer-map',
          component: () => import('../views/customer/Navbar/CustomerMap.vue'),
        },
        //Route for the mandatory password change for e.g refiller
        {
          path: 'password-change',
          name: 'customer-password-change',
          component: () =>
            import('../views/customer/CustomerPasswordChange.vue'),
        },
        {
          path: 'fridgeQrCodes',
          name: 'customer-fridge-qrcodes',
          component: () =>
            import('../views/customer/CustomerFridgeSelection.vue'),
        },
        {
          path: 'article/:id',
          name: 'customer-article-details',
          component: () =>
            import('@/views/customer/CustomerArticleDetails.vue'),
        },
        {
          path: 'help',
          name: 'customer-help',
          component: () =>
            import('@/views/customer/Navbar/CustomerHelp.vue'),
        },
        {
          path: 'how-it-works',
          name: 'how-it-works',
          component: () =>
            import('@/views/customer/Navbar/CustomerTutorial.vue'),
        },
      ],
    },
    // Supplier
    {
      path: '/supplier',
      meta: {
        roles: [Eatfresh_Basic_User_Type_Enum.Supplier],
      },
      component: () => import('@/views/supplier/SupplierLayout.vue'),
      children: [
        {
          path: '',
          redirect: { name: 'supplier-home' },
        },
        {
          path: 'login',
          name: 'supplier-login',
          component: () => import('@/views/supplier/SupplierLogin.vue'),
        },
        {
          path: 'home',
          name: 'supplier-home',
          component: () => import('@/views/supplier/SupplierHome.vue'),
        },
        {
          path: 'start-labeling',
          name: 'supplier-start-labeling',
          component: () =>
            import('@/views/supplier/labeling/SupplierStartLabeling.vue'),
        },
        {
          path: 'start-labeling-scanner-flow',
          name: 'supplier-start-labeling-scanner-flow',
          component: () =>
            import(
              '@/views/supplier/labeling/SupplierLabelingPrefillScanner.vue'
            ),
        },
        {
          path: 'tag-application-tutorial',
          name: 'supplier-tag-application-tutorial',
          component: () =>
            import(
              '@/views/supplier/labeling/SupplierTagApplicationTutorial.vue'
            ),
        },
        {
          path: 'labeling-in-progress/:id',
          name: 'supplier-labeling-in-progress',
          component: () =>
            import('@/views/supplier/labeling/SupplierLabelingInProgress.vue'),
        },
        {
          path: 'single-scan-mode/:id',
          name: 'supplier-single-scan-mode',
          component: () =>
            import('@/views/supplier/labeling/SupplierSingleScanMode.vue'),
        },
        {
          path: 'labeling-complete/:id',
          name: 'supplier-labeling-complete',
          component: () =>
            import('@/views/supplier/labeling/SupplierLabelingComplete.vue'),
        },
        {
          path: 'fridge-orders',
          name: 'supplier-fridge-orders',
          component: () =>
            import('@/views/supplier/commissioning/SupplierFridgeOrders.vue'),
        },
        {
          path: 'start-commissioning',
          name: 'supplier-start-commissioning',
          component: () =>
            import(
              '@/views/supplier/commissioning/SupplierStartCommissioning.vue'
            ),
        },
        {
          path: 'fridge-order/:id',
          name: 'supplier-fridge-order',
          component: () =>
            import('@/views/supplier/commissioning/SupplierFridgeOrder.vue'),
        },
        {
          path: 'fill-boxes',
          name: 'supplier-fill-boxes',
          component: () => import('@/views/supplier/SupplierFillBoxes.vue'),
        },
        {
          path: 'box-details',
          name: 'supplier-box-details',
          component: () => import('@/views/supplier/SupplierBoxDetails.vue'),
        },
        {
          path: 'commissioning-in-progress/:id',
          name: 'supplier-commissioning-in-progress',
          component: () =>
            import(
              '@/views/supplier/commissioning/SupplierCommissioningInProgress.vue'
            ),
        },
        {
          path: 'commissioning-complete/:id',
          name: 'supplier-commissioning-complete',
          component: () =>
            import(
              '@/views/supplier/commissioning/SupplierCommissioningComplete.vue'
            ),
        },
        {
          path: 'select-storage-container',
          name: 'supplier-select-storage-container',
          component: () =>
            import(
              '@/views/supplier/commissioning/SupplierSelectStorageContainer.vue'
            ),
        },
        {
          path: 'receipt-inspection/:id',
          name: 'supplier-receipt-inspection',
          component: () =>
            import('@/views/supplier/warehouse/SupplierReceiptInspection.vue'),
        },
        {
          path: 'slot',
          name: 'supplier-slot',
          component: () =>
            import('@/components/unreleased_features/SlotMachineContainer.vue'),
        },
        {
          path: 'goods-received',
          name: 'supplier-goods-received',
          component: () =>
            import('@/views/supplier/warehouse/SupplierGoodsReceived.vue'),
        },
        {
          path: 'process-shipment-tutorial/:warehouse_order_id',
          name: 'supplier-process-shipment-tutorial',
          component: () =>
            import('@/views/supplier/warehouse/ProcessShipmentTutorial.vue'),
        },
        {
          path: 'label-goods',
          name: 'supplier-label-shipment-goods',
          component: () =>
            import('@/views/supplier/warehouse/SupplierLabelOrder.vue'),
        },
        {
          path: 'quick-labeling',
          name: 'supplier-quick-labeling',
          component: () =>
            import('@/views/supplier/labeling/SupplierQuickLabeling.vue'),
        },
        {
          path: 'quick-labeling/:orderId',
          name: 'supplier-quick-labeling-flow',
          component: () =>
            import('@/views/supplier/labeling/SupplierQuickLabeling.vue'),
        },
        {
          path: 'external-orders',
          name: 'supplier-external-orders',
          component: () =>
            import('@/views/supplier/warehouse/SupplierExternalOrders.vue'),
        },
        {
          path: 'external-orders/:orderId/pick',
          name: 'supplier-guided-pick',
          component: () =>
            import('@/views/supplier/warehouse/SupplierGuidedPick.vue'),
        },
        {
          path: 'conflicting-positions',
          name: 'supplier-conflicting-labeling-order-positions',
          component: () =>
            import(
              '@/views/supplier/labeling/SupplierConflictingLabelingPositions.vue'
            ),
        },
      ],
    },
    //Refiller
    {
      path: '/refiller',
      meta: {
        roles: [Eatfresh_Basic_User_Type_Enum.Refiller],
      },
      component: () => import('@/views/refiller/RefillerLayout.vue'),
      children: [
        {
          path: '',
          redirect: { name: 'refiller-home' },
        },
        {
          path: 'home',
          name: 'refiller-home',
          component: () => import('@/views/refiller/RefillerHome.vue'),
        },
        {
          path: 'login',
          name: 'refiller-login',
          component: () => import('@/views/refiller/RefillerLogin.vue'),
        },
        {
          path: 'fridge-scanner',
          name: 'refiller-fridge-scanner',
          component: () => import('@/views/refiller/RefillerFridgeScanner.vue'),
        },
        {
          path: 'refill/:refill_id',
          children: [
            {
              path: 'clean-fridge',
              name: 'refiller-clean-fridge',
              component: () =>
                import('@/views/refiller/RefillerCleanFridge.vue'),
            },
            {
              path: 'refill-items',
              name: 'refiller-refill-items',
              component: () =>
                import('@/views/refiller/RefillerRefillProcess.vue'),
            },
            {
              path: 'expired-items',
              name: 'refiller-expired-items',
              component: () =>
                import('@/views/refiller/RefillerExpiredItems.vue'),
            },
            {
              path: 'refill-complete',
              name: 'refiller-refill-complete',
              component: () =>
                import('@/views/refiller/RefillerRefillConfirmation.vue'),
            },
          ],
        },
        {
          path: 'fridges-overview/:place_id',
          name: 'refiller-fridges-overview',
          component: () =>
            import('@/views/refiller/RefillerFridgesOverview.vue'),
        },
        {
          path: 'easy',
          name: 'refiller-easy',
          component: () => import('@/views/refiller/RefillerEasy.vue'),
        },
      ],
    },
    // B2B Wholesale
    {
      path: '/b2b',
      meta: {
        roles: [Eatfresh_Basic_User_Type_Enum.B2BWholesale],
      },
      component: () => import('@/views/b2b/B2bLayout.vue'),
      children: [
        {
          path: '',
          redirect: { name: 'b2b-orders' },
        },
        {
          meta: {
            // only allow unauthenticated users
            roles: [null],
          },
          path: 'login',
          name: 'b2b-login',
          component: () => import('@/views/b2b/B2bLogin.vue'),
        },
        /*{
          path: 'home',
          name: 'b2b-home',
          component: () => import('@/views/b2b/B2bHome.vue'),
        },*/
        {
          path: 'articles',
          name: 'b2b-articles',
          component: () => import('@/views/b2b/B2bArticles.vue'),
        },
        {
          path: 'orders',
          name: 'b2b-orders',
          component: () => import('@/views/b2b/B2bOrders.vue'),
        },
        {
          path: 'orders/:orderId',
          name: 'b2b-order-review',
          component: () => import('@/views/b2b/B2bOrderReview.vue'),
        },
        /*{
          path: 'locations',
          name: 'b2b-locations',
          component: () => import('@/views/b2b/B2bLocations.vue'),
        },*/
      ],
    },
    {
      path: '/01/:GTIN/21/:SERIAL',
      name: 'digital-link',
      component: () => import('../views/general/GS1DigitalLinkPage.vue'),
    },
    {
      path: '/:pathMatch(.*)*', // Matches any path that hasn't been matched yet
      redirect(from) {
        return { name: homeScreenForRoute(from) };
      },
    },
  ],
});

type App =
  | 'refiller'
  | 'supplier'
  | 'admin'
  | 'customer'
  | 'mock'
  | 'b2b'
  | null;

export function appForRoute(route: { path: string }): App {
  // Order matters: more-specific prefixes first to avoid substring collisions.
  if (route.path.startsWith('/b2b')) {
    return 'b2b';
  } else if (route.path.includes('refiller')) {
    return 'refiller';
  } else if (route.path.includes('supplier')) {
    return 'supplier';
  } else if (route.path.includes('admin')) {
    return 'admin';
  } else if (route.path.includes('customer')) {
    return 'customer';
  } else if (route.path.includes('mock')) {
    return 'mock';
  } else {
    return null;
  }
}

export function homeScreenForApp(app: App) {
  switch (app) {
    case 'refiller':
      return 'refiller-home';
    case 'customer':
      return 'customer-map';
    case 'admin':
      return 'admin-home';
    case 'supplier':
      return 'supplier-home';
    case 'mock':
      return 'mock-fridge-select';
    case 'b2b':
      return 'b2b-orders';
    default:
      return 'customer-map';
  }
}

export function appForRole(role: Eatfresh_Basic_User_Type_Enum | null): App {
  switch (role) {
    case Eatfresh_Basic_User_Type_Enum.Admin:
      return 'admin';
    case Eatfresh_Basic_User_Type_Enum.Refiller:
      return 'refiller';
    case Eatfresh_Basic_User_Type_Enum.Customer:
    case Eatfresh_Basic_User_Type_Enum.Anonymous:
      return 'customer';
    case Eatfresh_Basic_User_Type_Enum.Supplier:
      return 'supplier';
    case Eatfresh_Basic_User_Type_Enum.B2BWholesale:
      return 'b2b';
    default:
      return null;
  }
}

export function loginScreenForApp(app: App) {
  switch (app) {
    case 'admin':
      return 'admin-login';
    case 'customer':
      return 'customer-login';
    case 'mock':
      return 'mock-login';
    case 'refiller':
      return 'refiller-login';
    case 'supplier':
      return 'supplier-login';
    case 'b2b':
      return 'b2b-login';
    default:
      return 'customer-login';
  }
}

export function homeScreenForRoute(route: { path: string }) {
  return homeScreenForApp(appForRoute(route));
}

export function homeScreenForRole(role: Eatfresh_Basic_User_Type_Enum | null) {
  return homeScreenForApp(appForRole(role));
}

export function loginScreenForRoute(route: { path: string }) {
  return loginScreenForApp(appForRoute(route));
}

function isRoleAllowedToRoute(
  to: RouteLocationNormalizedGeneric,
  role: Eatfresh_Basic_User_Type_Enum | null,
) {
  const roles = to.meta.roles;
  if (Array.isArray(roles)) {
    return roles.includes(role);
  } else {
    return true;
  }
}

function deferredRerouteAfterTokenCheck(
  to: RouteLocationNormalized,
  currentRole: Eatfresh_Basic_User_Type_Enum | null,
) {
  const isAllowed = isRoleAllowedToRoute(to, currentRole);
  if (!isAllowed) {
    if (currentRole === null) {
      if (to.name !== loginScreenForRoute(to)) {
        router.push({
          name: loginScreenForRoute(to),
          query: {
            redirect: to.path,
          },
        });
      }
    } else {
      router.push({
        name: homeScreenForRole(currentRole),
      });
    }
  }
}

installHistoryTraversalTracking(router);

router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  const sharedStore = useSharedStore();

  fireEvent('page_view', { path: to.path, query: JSON.stringify(to.query) });

  sharedStore.routeLoading = true;
  if (!authStore.authToken && !authStore.initialized) {
    try {
      const restoredFromCookie = await authStore.restoreTokenFromCookie();
      authStore.setAuthToken(restoredFromCookie);
    } catch (e) {
      authStore.setAuthToken(null);
    }
    authStore.initialized = true;
  } else if (authStore.initialized) {
    // non-blocking check every route
    authStore
      .restoreTokenFromCookie()
      .then((restoredToken) => {
        authStore.setAuthToken(restoredToken);
        const currentRole = authStore.role;
        deferredRerouteAfterTokenCheck(to, currentRole);
      })
      .catch((e) => {
        authStore.setAuthToken(null);
        const currentRole = authStore.role;
        deferredRerouteAfterTokenCheck(to, currentRole);
      });
  }

  const currentRole = authStore.role;
  if (!isRoleAllowedToRoute(to, currentRole)) {
    if (currentRole === null) {
      if (to.name !== loginScreenForRoute(to)) {
        return {
          name: loginScreenForRoute(to),
          query: {
            redirect: to.path,
          },
        };
      }
    } else {
      return {
        name: homeScreenForRole(currentRole),
      };
    }
  }

  // now there is either a valid token in authstore or not.
  // admin-path validation
  // customer-path validation
  if (appForRoute(to) === 'customer' && currentRole) {
    if (
      to.name === 'customer-password-change' &&
      !authStore.requiresPasswordChange
    )
      return { name: homeScreenForRole(currentRole) };
    if (
      authStore.requiresPasswordChange === true &&
      to.name !== 'customer-password-change'
    ) {
      return { name: 'customer-password-change' };
    }
  }

  // supplier quick-labeling enforcement
  if (
    appForRoute(to) === 'supplier' &&
    currentRole === Eatfresh_Basic_User_Type_Enum.Supplier
  ) {
    const warehouseAssignmentStore = useWarehouseAssignmentStore();
    if (!warehouseAssignmentStore.loaded && authStore.id) {
      try {
        await warehouseAssignmentStore.fetch();
      } catch (e) {
        console.error('Failed to fetch warehouse assignment', e);
      }
    }
    if (warehouseAssignmentStore.enforceQuickLabeling) {
      const allowed = ['supplier-quick-labeling', 'supplier-quick-labeling-flow', 'supplier-login'];
      if (!allowed.includes(to.name as string)) {
        return { name: 'supplier-quick-labeling' };
      }
    }
  }

  return true;
});
router.afterEach(() => {
  const sharedStore = useSharedStore();
  sharedStore.routeLoading = false;
  // scroll main layout container to top when changing routes
  const mainScroll = document.querySelector('.content') as HTMLElement;
  if (mainScroll) {
    mainScroll.scrollTo({ top: 0, behavior: 'auto' });
  } else {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }
});
export default router;
